import React, { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const THUMB_WIDTH = 66;

function Thumbnail({ pdfDoc, pageNo, active, viewed, count, onSelect }) {
  const rootRef = useRef(null);
  const canvasRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!rootRef.current) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "300px" },
    );
    observer.observe(rootRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visible || !pdfDoc) return undefined;
    let cancelled = false;
    let task = null;

    (async () => {
      try {
        const page = await pdfDoc.getPage(pageNo);
        if (cancelled) return;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const base = page.getViewport({ scale: 1 });
        const viewport = page.getViewport({
          scale: (THUMB_WIDTH / base.width) * dpr,
        });
        const canvas = canvasRef.current;
        if (!canvas) return;
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        task = page.render({
          canvasContext: canvas.getContext("2d"),
          viewport,
        });
        await task.promise;
      } catch (error) {
        if (error?.name !== "RenderingCancelledException") console.error(error);
      }
    })();

    return () => {
      cancelled = true;
      task?.cancel();
    };
  }, [visible, pdfDoc, pageNo]);

  useEffect(() => {
    if (active)
      rootRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [active]);

  return (
    <Box
      ref={rootRef}
      component="button"
      type="button"
      aria-label={`Go to page ${pageNo}`}
      aria-current={active ? "page" : undefined}
      onClick={() => onSelect(pageNo)}
      sx={{
        display: "block",
        width: "100%",
        mb: 1,
        p: 0,
        border: 0,
        bgcolor: "transparent",
        cursor: "pointer",
        textAlign: "center",
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: THUMB_WIDTH,
          minHeight: 92,
          mx: "auto",
          bgcolor: "#fff",
          lineHeight: 0,
          outline: active ? "2px solid" : "1px solid",
          outlineColor: active ? "primary.main" : "#d5deea",
          borderRadius: 0.5,
          overflow: "hidden",
        }}
      >
        <canvas
          ref={canvasRef}
          style={{ width: "100%", height: "auto", display: "block" }}
        />
        {count > 0 && (
          <Box
            sx={{
              position: "absolute",
              top: 3,
              right: 3,
              minWidth: 17,
              height: 17,
              px: 0.4,
              borderRadius: 9,
              bgcolor: "primary.main",
              color: "#fff",
              fontSize: 10,
              fontWeight: 700,
              lineHeight: "17px",
            }}
          >
            {count}
          </Box>
        )}
      </Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        gap={0.25}
        mt={0.4}
      >
        <Typography variant="caption" fontWeight={active ? 700 : 500}>
          {pageNo}
        </Typography>
        {viewed && (
          <CheckCircleIcon sx={{ fontSize: 11, color: "success.main" }} />
        )}
      </Box>
    </Box>
  );
}

export default function PageThumbnails({
  pdfDoc,
  pageNo,
  viewedPages,
  counts,
  onSelect,
}) {
  const total = pdfDoc?.numPages || 0;

  return (
    <Box
      sx={{
        width: 96,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        bgcolor: "#f8fafd",
        borderRight: "1px solid #dfe7f3",
      }}
    >
      <Typography variant="caption" fontWeight={700} sx={{ px: 1.25, py: 1 }}>
        Pages ({total})
      </Typography>
      <Box flex={1} overflow="auto" px={0.5} pt={0.25}>
        {Array.from({ length: total }, (_, index) => index + 1).map(
          (number) => (
            <Thumbnail
              key={number}
              pdfDoc={pdfDoc}
              pageNo={number}
              active={number === pageNo}
              viewed={viewedPages.has(number)}
              count={counts[number] || 0}
              onSelect={onSelect}
            />
          ),
        )}
      </Box>
    </Box>
  );
}
